import { createSelector, createSlice } from "@reduxjs/toolkit";
import {
  AmountBucket,
  AmountBucketWithLabel,
  BorrowalVSNonBorrowalReportData,
  BorrowalVSNonBorrowalReportDataForAmountBucket,
  BorrowalVSNonBorrowalReportDataForDateWindow,
  BorrowalVSNonBorrowalReportResponse,
} from "../../../api/tenantInternalReports";
import { RootState } from "../../../app/store";
import { getCurrentMonthKey, lifeToDateWindowForMonth } from "../../../lib/utils";
import { pickWindowData } from "./reportWindows";
import { fetchBorrowalVsNonBorrowalReportThunk } from "./borrowalVsNonBorrowalReportThunk";

export const borrowalVsNonBorrowalBucketId = (bucket: AmountBucket): string =>
  `${bucket.gte ?? "min"}_${bucket.lt ?? "max"}`;

export interface BorrowalVsNonBorrowalReportState {
  buckets: BorrowalVSNonBorrowalReportDataForAmountBucket[];
  totals: BorrowalVSNonBorrowalReportDataForDateWindow[];
  reportName: string | null;
  exposureBuckets: AmountBucketWithLabel[];
  selectedPeriod: string | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: BorrowalVsNonBorrowalReportState = {
  buckets: [],
  totals: [],
  reportName: null,
  exposureBuckets: [],
  selectedPeriod: null,
  status: "idle",
  error: null,
};

const borrowalVsNonBorrowalReportSlice = createSlice({
  name: "borrowalVsNonBorrowalReport",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBorrowalVsNonBorrowalReportThunk.pending, (state) => {
        state.buckets = [];
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchBorrowalVsNonBorrowalReportThunk.fulfilled, (state, action) => {
        const report: BorrowalVSNonBorrowalReportResponse = action.payload;
        state.buckets = report.data_by_amount_buckets;
        state.totals = report.totals_by_date_window;
        state.reportName = report.config?.name ?? null;
        state.exposureBuckets = report.config?.exposure_buckets ?? [];
        state.selectedPeriod = action.meta.arg?.period ?? getCurrentMonthKey();
        state.status = "succeeded";
      })
      .addCase(fetchBorrowalVsNonBorrowalReportThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? "Failed to fetch fraud overview";
      });
  },
});

export const borrowalVsNonBorrowalReportReducer = borrowalVsNonBorrowalReportSlice.reducer;

export const selectBorrowalVsNonBorrowalBuckets = (state: RootState) =>
  state.borrowalVsNonBorrowalReportData.buckets;

export const selectBorrowalVsNonBorrowalTotals = (state: RootState) =>
  state.borrowalVsNonBorrowalReportData.totals;
export const selectBorrowalVsNonBorrowalReportName = (state: RootState) =>
  state.borrowalVsNonBorrowalReportData.reportName;
export const selectBorrowalVsNonBorrowalExposureBuckets = (state: RootState) =>
  state.borrowalVsNonBorrowalReportData.exposureBuckets;

export const selectBorrowalVsNonBorrowalBucketLabelById = createSelector(
  [selectBorrowalVsNonBorrowalExposureBuckets],
  (exposureBuckets): Record<string, string> =>
    exposureBuckets.reduce<Record<string, string>>((acc, bucket) => {
      acc[borrowalVsNonBorrowalBucketId(bucket)] = bucket.label;
      return acc;
    }, {})
);
export const selectBorrowalVsNonBorrowalSelectedPeriod = (state: RootState) =>
  state.borrowalVsNonBorrowalReportData.selectedPeriod;
export const selectBorrowalVsNonBorrowalStatus = (state: RootState) =>
  state.borrowalVsNonBorrowalReportData.status;
export const selectBorrowalVsNonBorrowalError = (state: RootState) =>
  state.borrowalVsNonBorrowalReportData.error;

export interface BorrowalVsNonBorrowalMetricSummary {
  totalCases: number | null;
  totalAmount: number | null;
}

const sum = (a: number | null, b: number | null): number | null =>
  a == null && b == null ? null : (a ?? 0) + (b ?? 0);


export const selectBorrowalVsNonBorrowalMetricSummary = createSelector(
  [selectBorrowalVsNonBorrowalSelectedPeriod, selectBorrowalVsNonBorrowalTotals],
  (period, totals): BorrowalVsNonBorrowalMetricSummary | null => {
    if (!period) return null;
    const cumulative: BorrowalVSNonBorrowalReportData | undefined = pickWindowData(
      totals,
      lifeToDateWindowForMonth(period)
    );
    if (!cumulative) return null;
    return {
      totalCases: sum(
        cumulative.num_cases_borrowal,
        cumulative.num_cases_non_borrowal
      ),
      totalAmount: sum(
        cumulative.total_amount_borrowal,
        cumulative.total_amount_non_borrowal
      ),
    };
  }
);
